import { filter, findIndex, forEach, map } from 'lodash';
import { produce } from 'immer';
import React, { FC } from 'react';
import { useDispatch } from 'react-redux';

import { Archive } from '../shared/rowActions/Archive';
import { Edit } from '../shared/rowActions/Edit';
import { nameWithAncestry } from 'src/presentation/components/pages/skills/hierarchy';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import SkillEditor from './SkillEditor';
import skillsResource from 'src/actions/skills/skillsResource';
import { StyledTR, StyledTD, RowActionContainer } from 'src/presentation/components/pages/shared/Table';
import SubTopicConnector from '@ad-scripts/presentation/components/reporting/report/lib/sub_topic_connector';
import TablePage, { TablePageRender } from '../shared/TablePage';
import { TD } from '@ad-scripts/presentation/components/common/table';
import theme from '@ad-scripts/presentation/themes/default';
import useSkills from 'src/presentation/hooks/useSkills';

const skillsDataAidPrefix = 'admin-skills-page';
const columnConfig = [{ attr: 'name', label: 'Name' }];
const tabConfig = [
  { label: 'Active', filter: skill => !skill.disabled, key: `${skillsDataAidPrefix}-tab-enabled` },
  { label: 'Archived', filter: skill => skill.disabled, key: `${skillsDataAidPrefix}-tab-disabled` },
];

const Skills: TablePageRender = ({ root }) => {
  const { errors, dataMergeFinished, values } = useSkills();
  const actions = (
    <RouterLinkButton data-aid={`${skillsDataAidPrefix}-addNew`} to={`${root}/new`}>
      Create Skill
    </RouterLinkButton>
  );

  const description = (
    <span>
      Skills can be used with People Match to connect customers to agents with the desired expertise, like languages or
      product specialty. You can create nested Skills to organize a range of Skill areas. To learn more about Skills,
      visit{' '}
      <a target="_blank" rel="noopener noreferrer" href="https://help.gladly.com/docs/what-is-skills">
        Help Docs
      </a>
      .
    </span>
  );

  return (
    <TablePage
      actions={actions}
      columnConfig={columnConfig}
      data-aid={`${skillsDataAidPrefix}`}
      description={description}
      editor={SkillEditor}
      errors={errors}
      filterFunc={customFilter}
      isLoading={!dataMergeFinished}
      items={values}
      name="Skills"
      renderRow={renderRow}
      resetErrors={skillsResource.actions.resetErrors}
      root={root}
      tabConfig={tabConfig}
    />
  );
};

export function renderRow(skill) {
  return (
    <SkillHierarchyRow
      columnConfig={columnConfig}
      dataAid={skillsDataAidPrefix}
      depth={skill.depth}
      item={skill}
      key={skill.id}
    />
  );
}

export function customFilter(allSkills, searchString, tab) {
  const tabFilteredSkills = tabFilter(allSkills, tab);
  const searchFilteredSkills = searchString ? searchFilter(tabFilteredSkills, searchString) : tabFilteredSkills;

  const skills = map(searchFilteredSkills, skill => {
    skill.depth = 0;
    return skill;
  });

  map(skills, item => descendantsWithDepth(skills, item.id, 0));

  return skills;
}

// Helper Functions

function tabFilter(allItems, selectedTab) {
  return filter(allItems, item => {
    return selectedTab === 'Archived' ? item.disabled : !item.disabled;
  });
}

function searchFilter(skills, searchString) {
  return filter(skills, skill =>
    skill.ancestralNames ? skill.ancestralNames.some(includesSearchString) : includesSearchString(skill.name)
  );

  function includesSearchString(receiver: string) {
    return receiver.toLowerCase().includes(searchString.toLowerCase());
  }
}

function descendantsWithDepth(items, skillId, depth) {
  const itemIndex = findIndex(items, ['id', skillId]);

  if (itemIndex >= 0 && items[itemIndex].depth < depth) {
    items[itemIndex].depth = depth;
  }

  if (itemIndex >= 0 && items[itemIndex].children && items[itemIndex].children.length > 0) {
    forEach(items[itemIndex].children, id => descendantsWithDepth(items, id, depth + 1));
  }
}

// Custom row to display skill hierarchy in Table List

type SkillHierarchyRowProps = {
  columnConfig: any;
  dataAid: string;
  depth: number;
  item: any;
};

const SkillHierarchyRow: FC<SkillHierarchyRowProps> = props => {
  const { columnConfig, dataAid, item, depth } = props;
  const dispatch = useDispatch();

  const style = { marginLeft: `calc(${depth - 1} * ${theme.fontSize.base} + ${theme.spacing.medium})` };
  const cells = map(columnConfig, ({ attr }) => (
    <StyledTD data-aid={`${dataAid}-row-${item.id}-${attr}`} key={attr}>
      {depth > 0 ? <SubTopicConnector style={style} /> : null}
      {depth > 0 ? item.name : nameWithAncestry(item)}
    </StyledTD>
  ));

  const toggleArchive = () => {
    const newDisabledState = !item.disabled;
    const toast = newDisabledState ? `We archived ${item.name}.` : `We unarchived ${item.name}.`;

    const updatedItem = produce(item, draft => {
      draft.disabled = newDisabledState;
      draft._version++;
    });

    dispatch(
      skillsResource.effects.update({ id: item.id }, updatedItem, { customToastMsg: toast, disableWindowBack: true })
    );
  };

  return (
    <StyledTR data-aid={`${dataAid}-row-${item.id}`} key={item.id}>
      {cells}
      <TD style={{ width: '1%' }}>
        <RowActionContainer>
          {!item.disabled ? (
            <Archive
              confirmationText={`Archive ${item.name} ?`}
              description={
                <>
                  Archiving this Skill will make it unavailable to use in Matching, and associating with Users.
                  <br />
                  Any Skills nested under this Skill will also be archived.
                </>
              }
              submitText="Archive Skill"
              itemId={item.id}
              onArchive={toggleArchive}
            />
          ) : (
            <Archive
              confirmationText={`Unarchive ${item.name} ?`}
              description={
                <>
                  Unarchiving this Skill will make it available for use in Matching.
                  <br />
                  Unarchived Skills continue to be associated with Users who had the Skill before it was archived.
                </>
              }
              submitText="Unarchive Skill"
              itemId={item.id}
              onArchive={toggleArchive}
              isUnarchive
            />
          )}
          <Edit dataAid={`editLink-${item.id}`} id={item.id} />
        </RowActionContainer>
      </TD>
    </StyledTR>
  );
};

export default Skills;
